/**
 * DriverOnboarding - Componente de Onboarding de Motorista (Etapa 2)
 * Coleta: Dados Pessoais, Endereço, Veículo, PIX/Profissional e Declarações
 * Design: Background branco, cards verde claro, botões verdes
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import {
    onboardingSchema,
    OnboardingFormData,
    PIX_KEY_TYPES,
    MOBILITY_APPS,
    AVAILABLE_CITIES
} from '../schemas/onboardingSchema';
import { maskCPF, maskCEP, maskVehiclePlate } from '../utils/validators';
import {
    fetchAddressByCEP,
    fetchVehicleBrands,
    fetchVehicleModels,
    getVehicleColors,
    VehicleBrand,
    VehicleModel
} from '../utils/api';
import {
    Loader2,
    User,
    MapPin,
    Car,
    Wallet,
    FileCheck,
    CheckCircle2,
    AlertCircle,
    ChevronDown,
    ChevronUp,
    X,
    Plus,
    Check,
    Camera
} from 'lucide-react';
import { ImageCapture } from './ImageCapture';
import { FileUpload } from './FileUpload';

type Section = 'personal' | 'address' | 'vehicle' | 'professional' | 'declarations';

export function DriverOnboarding() {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [activeSection, setActiveSection] = useState<Section>('personal');
    const [completedSections, setCompletedSections] = useState<Set<Section>>(new Set());
    const [userId, setUserId] = useState<string>('');

    const [isFetchingCEP, setIsFetchingCEP] = useState(false);
    const [cepError, setCepError] = useState<string | null>(null);

    const [brands, setBrands] = useState<VehicleBrand[]>([]);
    const [models, setModels] = useState<VehicleModel[]>([]);
    const [isFetchingBrands, setIsFetchingBrands] = useState(false);
    const [isFetchingModels, setIsFetchingModels] = useState(false);
    const [selectedBrandCode, setSelectedBrandCode] = useState<string>('');

    const colors = getVehicleColors();

    const {
        register,
        handleSubmit,
        setValue,
        watch,
        trigger,
        control,
        formState: { errors }
    } = useForm<OnboardingFormData>({
        resolver: zodResolver(onboardingSchema),
        mode: 'onChange',
        defaultValues: {
            selfie_url: '',
            app_profile_screenshot_url: '',
            cnh_photo_url: '',
            vehicle_year: new Date().getFullYear(),
            mobility_apps: [],
            operating_cities: [],
            confirm_driver_status: false,
            confirm_tablet_investment: false,
            confirm_contact_agreement: false,
            confirm_info_accuracy: false,
        }
    });

    const cpfValue = watch('cpf');
    const cepValue = watch('zip_code');
    const plateValue = watch('vehicle_plate');
    const selectedBrand = watch('vehicle_brand');
    const mobilityApps = watch('mobility_apps') || [];
    const operatingCities = watch('operating_cities') || [];
    const pixKeyType = watch('pix_key_type');
    const selfieUrl = watch('selfie_url');
    const appProfileUrl = watch('app_profile_screenshot_url');
    const cnhPhotoUrl = watch('cnh_photo_url');

    // Obter userId do Supabase e carregar dados existentes se for correção
    useEffect(() => {
        async function loadUserData() {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user?.id) return;

            setUserId(user.id);

            // Buscar dados existentes do motorista (para correção ou edição)
            const { data: driverData, error } = await supabase
                .from('drivers')
                .select('*')
                .eq('id', user.id)
                .single();

            if (error || !driverData) {
                console.log('[DriverOnboarding] Novo cadastro');
                return;
            }

            // Se tem dados, preencher o formulário
            // Isso permite edição quando status é pending_correction ou pending_submission
            if (driverData.profile_status === 'pending_correction' || driverData.cpf) {
                console.log('[DriverOnboarding] Carregando dados existentes para edição');

                // Dados pessoais
                if (driverData.selfie_url) setValue('selfie_url', driverData.selfie_url);
                if (driverData.app_profile_screenshot_url) setValue('app_profile_screenshot_url', driverData.app_profile_screenshot_url);
                if (driverData.cnh_photo_url) setValue('cnh_photo_url', driverData.cnh_photo_url);
                if (driverData.cpf) setValue('cpf', driverData.cpf);
                if (driverData.rg) setValue('rg', driverData.rg);
                if (driverData.cnh_number) setValue('cnh_number', driverData.cnh_number);

                // Endereço
                if (driverData.zip_code) setValue('zip_code', driverData.zip_code);
                if (driverData.street) setValue('street', driverData.street);
                if (driverData.number) setValue('number', driverData.number);
                if (driverData.complement) setValue('complement', driverData.complement);
                if (driverData.neighborhood) setValue('neighborhood', driverData.neighborhood);
                if (driverData.cidade || driverData.city) setValue('city', driverData.cidade || driverData.city);
                if (driverData.estado || driverData.state) setValue('state', driverData.estado || driverData.state);

                // Veículo
                if (driverData.vehicle_plate) setValue('vehicle_plate', driverData.vehicle_plate);
                if (driverData.vehicle_brand) setValue('vehicle_brand', driverData.vehicle_brand);
                if (driverData.vehicle_model) setValue('vehicle_model', driverData.vehicle_model);
                if (driverData.vehicle_color) setValue('vehicle_color', driverData.vehicle_color);
                if (driverData.vehicle_year) setValue('vehicle_year', driverData.vehicle_year);
                if (driverData.renavam) setValue('renavam', driverData.renavam);

                // PIX e Profissional
                if (driverData.pix_key_type) setValue('pix_key_type', driverData.pix_key_type);
                if (driverData.pix_key) setValue('pix_key', driverData.pix_key);
                if (driverData.mobility_apps) setValue('mobility_apps', driverData.mobility_apps);
                if (driverData.operating_cities) setValue('operating_cities', driverData.operating_cities);
                // Confirmar status de motorista (profissional)
                if (driverData.confirm_driver_status) setValue('confirm_driver_status', driverData.confirm_driver_status);

                // Declarações (precisam ser reconfirmadas na correção)
                setValue('confirm_tablet_investment', false);
                setValue('confirm_contact_agreement', false);
                setValue('confirm_info_accuracy', false);

                // Se é uma correção, marcar TODAS as seções como completas para permitir navegação livre
                // e abrir diretamente na seção de declarações
                if (driverData.profile_status === 'pending_correction') {
                    setCompletedSections(new Set(['personal', 'address', 'vehicle', 'professional']));
                    setActiveSection('declarations');
                } else {
                    // Para outros casos, marcar seções com dados como completas
                    const sectionsToComplete: Section[] = [];
                    if (driverData.cpf) sectionsToComplete.push('personal');
                    if (driverData.zip_code && driverData.street) sectionsToComplete.push('address');
                    if (driverData.vehicle_plate) sectionsToComplete.push('vehicle');
                    if (driverData.pix_key) sectionsToComplete.push('professional');
                    setCompletedSections(new Set(sectionsToComplete));
                }
            }
        }
        loadUserData();
    }, [setValue]);

    useEffect(() => {
        async function loadBrands() {
            setIsFetchingBrands(true);
            const brandList = await fetchVehicleBrands();
            setBrands(brandList);
            setIsFetchingBrands(false);
        }
        loadBrands();
    }, []);

    useEffect(() => {
        async function loadModels() {
            if (!selectedBrandCode) {
                setModels([]);
                return;
            }
            setIsFetchingModels(true);
            const modelList = await fetchVehicleModels(selectedBrandCode);
            setModels(modelList);
            setIsFetchingModels(false);
        }
        loadModels();
    }, [selectedBrandCode]);

    const handleCPFChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const masked = maskCPF(e.target.value);
        setValue('cpf', masked, { shouldValidate: true });
    };

    const handleCEPChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const masked = maskCEP(e.target.value);
        setValue('zip_code', masked, { shouldValidate: true });

        if (masked.length === 9) {
            await searchCEP(masked);
        } else {
            setCepError(null);
        }
    };

    const handlePlateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const masked = maskVehiclePlate(e.target.value);
        setValue('vehicle_plate', masked, { shouldValidate: true });
    };

    const handleBrandChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const brandName = e.target.value;
        const brand = brands.find(b => b.nome === brandName);

        setValue('vehicle_brand', brandName, { shouldValidate: true });
        setValue('vehicle_model', '', { shouldValidate: false });
        setSelectedBrandCode(brand?.valor || '');
        setModels([]);
    };

    const searchCEP = useCallback(async (cep: string) => {
        setIsFetchingCEP(true);
        setCepError(null);

        const address = await fetchAddressByCEP(cep);

        if (address) {
            setValue('street', address.street, { shouldValidate: true });
            setValue('neighborhood', address.neighborhood, { shouldValidate: true });
            setValue('city', address.city, { shouldValidate: true });
            setValue('state', address.state, { shouldValidate: true });
            toast.success('Endereço encontrado!');
            setTimeout(() => {
                document.getElementById('address-number')?.focus();
            }, 100);
        } else {
            setCepError('CEP não encontrado. Preencha manualmente.');
            toast.error('CEP não encontrado');
        }

        setIsFetchingCEP(false);
    }, [setValue]);

    // Toggle para aplicativos de mobilidade
    const toggleMobilityApp = (app: string) => {
        const current = mobilityApps || [];
        if (current.includes(app)) {
            setValue('mobility_apps', current.filter(a => a !== app), { shouldValidate: true });
        } else {
            setValue('mobility_apps', [...current, app], { shouldValidate: true });
        }
    };

    // Toggle para cidades de atuação
    const toggleOperatingCity = (cityValue: string) => {
        const current = operatingCities || [];
        if (current.includes(cityValue)) {
            setValue('operating_cities', current.filter(c => c !== cityValue), { shouldValidate: true });
        } else {
            setValue('operating_cities', [...current, cityValue], { shouldValidate: true });
        }
    };

    const validateSection = async (section: Section): Promise<boolean> => {
        const fieldsToValidate: Record<Section, (keyof OnboardingFormData)[]> = {
            personal: ['selfie_url', 'app_profile_screenshot_url', 'cnh_photo_url', 'cpf', 'rg', 'cnh_number'],
            address: ['zip_code', 'street', 'number', 'neighborhood', 'city', 'state'],
            vehicle: ['vehicle_plate', 'vehicle_brand', 'vehicle_model', 'vehicle_color', 'vehicle_year', 'renavam'],
            professional: ['pix_key_type', 'pix_key', 'mobility_apps', 'operating_cities', 'confirm_driver_status'],
            declarations: ['confirm_tablet_investment', 'confirm_contact_agreement', 'confirm_info_accuracy']
        };
        return await trigger(fieldsToValidate[section]);
    };

    // Verifica se pode acessar uma seção
    const canAccessSection = (section: Section): boolean => {
        if (section === 'personal') return true;
        if (section === 'address') return completedSections.has('personal');
        if (section === 'vehicle') return completedSections.has('personal') && completedSections.has('address');
        if (section === 'professional') return completedSections.has('personal') && completedSections.has('address') && completedSections.has('vehicle');
        if (section === 'declarations') return completedSections.has('personal') && completedSections.has('address') && completedSections.has('vehicle') && completedSections.has('professional');
        return false;
    };

    const handleSectionToggle = async (section: Section) => {
        if (completedSections.has(section) || section === activeSection) {
            setActiveSection(section);
            return;
        }
        if (canAccessSection(section)) {
            setActiveSection(section);
        } else {
            toast.error('Complete a etapa atual antes de prosseguir');
        }
    };

    const goToNextSection = async () => {
        const isCurrentValid = await validateSection(activeSection);
        if (!isCurrentValid) {
            toast.error('Preencha todos os campos corretamente');
            return;
        }

        setCompletedSections(prev => new Set([...prev, activeSection]));

        if (activeSection === 'personal') setActiveSection('address');
        else if (activeSection === 'address') setActiveSection('vehicle');
        else if (activeSection === 'vehicle') setActiveSection('professional');
        else if (activeSection === 'professional') setActiveSection('declarations');
    };

    const goToPreviousSection = () => {
        if (activeSection === 'address') setActiveSection('personal');
        else if (activeSection === 'vehicle') setActiveSection('address');
        else if (activeSection === 'professional') setActiveSection('vehicle');
        else if (activeSection === 'declarations') setActiveSection('professional');
    };

    const onSubmit = async (data: OnboardingFormData) => {
        try {
            setIsLoading(true);

            const { data: { user }, error: userError } = await supabase.auth.getUser();

            if (userError || !user) {
                toast.error('Sessão expirada. Faça login novamente.');
                navigate('/driver/login');
                return;
            }

            const { error: updateError } = await supabase
                .from('drivers')
                .update({
                    // Fotos e Documentos Visuais
                    selfie_url: data.selfie_url,
                    app_profile_screenshot_url: data.app_profile_screenshot_url,
                    cnh_photo_url: data.cnh_photo_url,

                    // Documentos
                    cpf: data.cpf,
                    rg: data.rg,
                    cnh: parseInt(data.cnh_number.replace(/\D/g, ''), 10),
                    cnh_number: data.cnh_number,

                    // Endereço
                    cidade: data.city,
                    estado: data.state,
                    zip_code: data.zip_code,
                    street: data.street,
                    number: data.number,
                    complement: data.complement || null,
                    neighborhood: data.neighborhood,
                    city: data.city,
                    state: data.state,

                    // Veículo
                    vehicle_plate: data.vehicle_plate.toUpperCase(),
                    vehicle_brand: data.vehicle_brand,
                    vehicle_model: data.vehicle_model,
                    vehicle_color: data.vehicle_color,
                    vehicle_year: data.vehicle_year,
                    renavam: data.renavam,

                    // PIX e Profissional
                    pix_key_type: data.pix_key_type,
                    pix_key: data.pix_key,
                    mobility_apps: data.mobility_apps,
                    operating_cities: data.operating_cities,

                    // Declarações
                    confirm_driver_status: data.confirm_driver_status,
                    confirm_tablet_investment: data.confirm_tablet_investment,
                    confirm_contact_agreement: data.confirm_contact_agreement,
                    confirm_info_accuracy: data.confirm_info_accuracy,

                    // Status - Muda para pending_review aguardando aprovação do admin
                    profile_status: 'pending_review',
                    // Limpar mensagem de correção anterior
                    correction_message: null
                })
                .eq('id', user.id);

            if (updateError) {
                console.error('[DriverOnboarding] Erro ao atualizar:', updateError);
                if (updateError.code === '23505') {
                    if (updateError.message.includes('cpf')) toast.error('Este CPF já está cadastrado');
                    else if (updateError.message.includes('cnh')) toast.error('Esta CNH já está cadastrada');
                    else if (updateError.message.includes('vehicle_plate')) toast.error('Esta placa já está cadastrada');
                    else if (updateError.message.includes('renavam')) toast.error('Este Renavam já está cadastrado');
                    else toast.error('Dados duplicados encontrados');
                } else {
                    toast.error('Erro ao salvar dados. Tente novamente.');
                }
                return;
            }

            toast.success('Cadastro enviado com sucesso!');
            setTimeout(() => navigate('/driver/review'), 1500);

        } catch (error) {
            console.error('[DriverOnboarding] Erro inesperado:', error);
            toast.error('Erro inesperado. Tente novamente.');
        } finally {
            setIsLoading(false);
        }
    };

    const getSectionNumber = (section: Section): number => {
        const sections: Section[] = ['personal', 'address', 'vehicle', 'professional', 'declarations'];
        return sections.indexOf(section) + 1;
    };

    const getPixPlaceholder = (): string => {
        switch (pixKeyType) {
            case 'celular': return '(00) 99999-9999';
            case 'email': return 'seuemail@exemplo.com';
            case 'cpf': return '999.999.999-99';
            case 'cnpj': return '99.999.999/9999-99';
            case 'aleatoria': return 'Sua chave aleatória';
            default: return 'Informe sua chave PIX';
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-emerald-50/30 to-gray-50">
            {/* Header */}
            <div className="bg-white shadow-sm sticky top-0 z-10 border-b border-emerald-100">
                <div className="max-w-lg mx-auto px-4 py-6">

                    {/* Título e Subtítulo */}
                    <div className="text-center mb-6">
                        <h1 className="text-2xl font-bold text-gray-800">
                            Complete seu cadastro
                        </h1>
                        <p className="text-gray-500 mt-1">
                            Preencha os dados em 5 passos
                        </p>
                    </div>

                    {/* Steps Indicator - 5 etapas */}
                    <div className="flex items-center justify-center gap-1">
                        <StepIndicator
                            step={1}
                            label="Pessoal"
                            active={activeSection === 'personal'}
                            completed={completedSections.has('personal')}
                        />
                        <div className="w-4 sm:w-6 h-0.5 bg-gray-200" />
                        <StepIndicator
                            step={2}
                            label="Endereço"
                            active={activeSection === 'address'}
                            completed={completedSections.has('address')}
                        />
                        <div className="w-4 sm:w-6 h-0.5 bg-gray-200" />
                        <StepIndicator
                            step={3}
                            label="Veículo"
                            active={activeSection === 'vehicle'}
                            completed={completedSections.has('vehicle')}
                        />
                        <div className="w-4 sm:w-6 h-0.5 bg-gray-200" />
                        <StepIndicator
                            step={4}
                            label="Profissional"
                            active={activeSection === 'professional'}
                            completed={completedSections.has('professional')}
                        />
                        <div className="w-4 sm:w-6 h-0.5 bg-gray-200" />
                        <StepIndicator
                            step={5}
                            label="Termos"
                            active={activeSection === 'declarations'}
                            completed={completedSections.has('declarations')}
                        />
                    </div>
                </div>
            </div>

            {/* Formulário */}
            <form onSubmit={handleSubmit(onSubmit)} className="max-w-lg mx-auto p-4 pb-24">

                {/* SEÇÃO 1: DADOS PESSOAIS */}
                <SectionCard
                    title="Dados Pessoais"
                    icon={<User className="w-5 h-5" />}
                    isOpen={activeSection === 'personal'}
                    onToggle={() => handleSectionToggle('personal')}
                    stepNumber={1}
                    totalSteps={5}
                    completed={completedSections.has('personal')}
                >
                    <div className="space-y-5">
                        {/* Selfie do Motorista */}
                        <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl p-4 border border-emerald-100">
                            <div className="mb-3 text-xs text-emerald-700 bg-emerald-100/50 rounded-lg px-3 py-2">
                                <span><strong>Dica:</strong> Tire a foto em um local bem iluminado, de preferência com luz natural, para garantir uma imagem nítida.</span>
                            </div>
                            <ImageCapture
                                value={selfieUrl}
                                onChange={(url) => setValue('selfie_url', url, { shouldValidate: true })}
                                userId={userId}
                                label="Foto do Rosto (Selfie)"
                                placeholder="Tire uma selfie do seu rosto"
                                error={errors.selfie_url?.message}
                                required
                            />
                        </div>

                        {/* Print do Perfil do App */}
                        <div className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-xl p-4 border border-teal-100">
                            <FileUpload
                                value={appProfileUrl}
                                onChange={(url) => setValue('app_profile_screenshot_url', url, { shouldValidate: true })}
                                userId={userId}
                                label="Print do Perfil no App de Mobilidade"
                                placeholder="Envie um print do seu perfil"
                                description="Faça um print da tela do seu perfil no Uber, 99, InDriver ou outro app que mostre suas corridas, nota ou foto no app."
                                folder="app-profile-screenshots"
                                error={errors.app_profile_screenshot_url?.message}
                                required
                            />
                        </div>

                        <div className="border-t border-gray-200 pt-4">
                            <p className="text-sm text-gray-500 mb-4 text-center">
                                Agora preencha seus dados pessoais
                            </p>
                        </div>

                        {/* CPF */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                CPF <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                value={cpfValue || ''}
                                onChange={handleCPFChange}
                                placeholder="999.999.999-99"
                                maxLength={14}
                                className={inputClassName(errors.cpf)}
                            />
                            {errors.cpf && <ErrorMessage message={errors.cpf.message} />}
                        </div>

                        {/* RG */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                RG <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                {...register('rg')}
                                placeholder="Número do RG"
                                className={inputClassName(errors.rg)}
                            />
                            {errors.rg && <ErrorMessage message={errors.rg.message} />}
                        </div>

                        {/* CNH */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                CNH (11 dígitos) <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                {...register('cnh_number')}
                                placeholder="00000000000"
                                maxLength={11}
                                className={inputClassName(errors.cnh_number)}
                            />
                            {errors.cnh_number && <ErrorMessage message={errors.cnh_number.message} />}
                        </div>

                        {/* Foto da CNH */}
                        <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl p-4 border border-emerald-100">
                            <div className="mb-3 text-xs text-emerald-700 bg-emerald-100/50 rounded-lg px-3 py-2 space-y-1">
                                <p className="font-semibold">Orientações para foto da CNH:</p>
                                <ul className="list-disc list-inside space-y-0.5 ml-2">
                                    <li>Abra a CNH mostrando frente e verso</li>
                                    <li>Posicione sobre uma superfície plana</li>
                                    <li>Certifique-se que todos os dados estão legíveis</li>
                                    <li>Evite reflexos e sombras sobre o documento</li>
                                </ul>
                            </div>
                            <ImageCapture
                                value={cnhPhotoUrl}
                                onChange={(url) => setValue('cnh_photo_url', url, { shouldValidate: true })}
                                userId={userId}
                                label="Foto da CNH Aberta"
                                placeholder="Tire uma foto da sua CNH"
                                error={errors.cnh_photo_url?.message}
                                required
                                useFrontCamera={false}
                            />
                        </div>

                        <button
                            type="button"
                            onClick={goToNextSection}
                            className="w-full py-3.5 bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white font-semibold rounded-xl transition-all shadow-lg shadow-emerald-200 hover:shadow-emerald-300"
                        >
                            Continuar
                        </button>
                    </div>
                </SectionCard>

                {/* SEÇÃO 2: ENDEREÇO */}
                <SectionCard
                    title="Endereço"
                    icon={<MapPin className="w-5 h-5" />}
                    isOpen={activeSection === 'address'}
                    onToggle={() => handleSectionToggle('address')}
                    stepNumber={2}
                    totalSteps={5}
                    completed={completedSections.has('address')}
                >
                    <div className="space-y-4">
                        {/* CEP */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                CEP <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <input
                                    type="text"
                                    value={cepValue || ''}
                                    onChange={handleCEPChange}
                                    placeholder="99999-999"
                                    maxLength={9}
                                    className={inputClassName(errors.zip_code || cepError)}
                                />
                                {isFetchingCEP && (
                                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-green-500 animate-spin" />
                                )}
                                {!isFetchingCEP && !errors.zip_code && cepValue?.length === 9 && !cepError && (
                                    <CheckCircle2 className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-green-500" />
                                )}
                            </div>
                            {errors.zip_code && <ErrorMessage message={errors.zip_code.message} />}
                            {cepError && <ErrorMessage message={cepError} />}
                        </div>

                        {/* Rua */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                Rua <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                {...register('street')}
                                placeholder="Nome da rua"
                                className={inputClassName(errors.street)}
                            />
                            {errors.street && <ErrorMessage message={errors.street.message} />}
                        </div>

                        {/* Número e Complemento */}
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                    Número <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    id="address-number"
                                    {...register('number')}
                                    placeholder="123"
                                    className={inputClassName(errors.number)}
                                />
                                {errors.number && <ErrorMessage message={errors.number.message} />}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                    Complemento
                                </label>
                                <input
                                    type="text"
                                    {...register('complement')}
                                    placeholder="Apto, Bloco..."
                                    className={inputClassName(false)}
                                />
                            </div>
                        </div>

                        {/* Bairro */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                Bairro <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                {...register('neighborhood')}
                                placeholder="Nome do bairro"
                                className={inputClassName(errors.neighborhood)}
                            />
                            {errors.neighborhood && <ErrorMessage message={errors.neighborhood.message} />}
                        </div>

                        {/* Cidade e Estado */}
                        <div className="grid grid-cols-3 gap-3">
                            <div className="col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                    Cidade <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    {...register('city')}
                                    placeholder="Nome da cidade"
                                    className={inputClassName(errors.city)}
                                />
                                {errors.city && <ErrorMessage message={errors.city.message} />}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                    UF <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    {...register('state')}
                                    placeholder="SP"
                                    maxLength={2}
                                    className={`${inputClassName(errors.state)} uppercase`}
                                />
                                {errors.state && <ErrorMessage message={errors.state.message} />}
                            </div>
                        </div>

                        <div className="flex gap-3">
                            <button
                                type="button"
                                onClick={goToPreviousSection}
                                className="flex-1 py-3 border-2 border-gray-200 text-gray-600 font-semibold rounded-xl hover:bg-gray-50 transition-all"
                            >
                                Voltar
                            </button>
                            <button
                                type="button"
                                onClick={goToNextSection}
                                className="flex-1 py-3.5 bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white font-semibold rounded-xl transition-all shadow-lg shadow-emerald-200"
                            >
                                Continuar
                            </button>
                        </div>
                    </div>
                </SectionCard>

                {/* SEÇÃO 3: DADOS DO VEÍCULO */}
                <SectionCard
                    title="Dados do Veículo"
                    icon={<Car className="w-5 h-5" />}
                    isOpen={activeSection === 'vehicle'}
                    onToggle={() => handleSectionToggle('vehicle')}
                    stepNumber={3}
                    totalSteps={5}
                    completed={completedSections.has('vehicle')}
                >
                    <div className="space-y-4">
                        {/* Placa */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                Placa do Veículo <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                value={plateValue || ''}
                                onChange={handlePlateChange}
                                placeholder="ABC-1234 ou ABC-1D23"
                                maxLength={8}
                                className={`${inputClassName(errors.vehicle_plate)} uppercase`}
                            />
                            {errors.vehicle_plate && <ErrorMessage message={errors.vehicle_plate.message} />}
                        </div>

                        {/* Marca */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                Marca <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <select
                                    value={selectedBrand || ''}
                                    onChange={handleBrandChange}
                                    disabled={isFetchingBrands}
                                    className={selectClassName(errors.vehicle_brand)}
                                >
                                    <option value="">Selecione a marca</option>
                                    {brands.map((brand) => (
                                        <option key={brand.valor} value={brand.nome}>
                                            {brand.nome}
                                        </option>
                                    ))}
                                </select>
                                {isFetchingBrands && (
                                    <Loader2 className="absolute right-10 top-1/2 -translate-y-1/2 w-5 h-5 text-green-500 animate-spin" />
                                )}
                            </div>
                            {errors.vehicle_brand && <ErrorMessage message={errors.vehicle_brand.message} />}
                        </div>

                        {/* Modelo */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                Modelo <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <select
                                    {...register('vehicle_model')}
                                    disabled={!selectedBrand || isFetchingModels}
                                    className={selectClassName(errors.vehicle_model)}
                                >
                                    <option value="">
                                        {!selectedBrand ? 'Selecione a marca primeiro' : isFetchingModels ? 'Carregando...' : 'Selecione o modelo'}
                                    </option>
                                    {models.map((model) => (
                                        <option key={model.valor} value={model.nome}>
                                            {model.nome}
                                        </option>
                                    ))}
                                </select>
                                {isFetchingModels && (
                                    <Loader2 className="absolute right-10 top-1/2 -translate-y-1/2 w-5 h-5 text-green-500 animate-spin" />
                                )}
                            </div>
                            {errors.vehicle_model && <ErrorMessage message={errors.vehicle_model.message} />}
                        </div>

                        {/* Cor e Ano */}
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                    Cor <span className="text-red-500">*</span>
                                </label>
                                <select
                                    {...register('vehicle_color')}
                                    className={selectClassName(errors.vehicle_color)}
                                >
                                    <option value="">Selecione</option>
                                    {colors.map((color) => (
                                        <option key={color} value={color}>{color}</option>
                                    ))}
                                </select>
                                {errors.vehicle_color && <ErrorMessage message={errors.vehicle_color.message} />}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                    Ano <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="number"
                                    {...register('vehicle_year', { valueAsNumber: true })}
                                    placeholder="2024"
                                    min={1990}
                                    max={new Date().getFullYear() + 1}
                                    className={inputClassName(errors.vehicle_year)}
                                />
                                {errors.vehicle_year && <ErrorMessage message={errors.vehicle_year.message} />}
                            </div>
                        </div>

                        {/* Renavam */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                Renavam (11 dígitos) <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                {...register('renavam')}
                                placeholder="00000000000"
                                maxLength={11}
                                className={inputClassName(errors.renavam)}
                            />
                            {errors.renavam && <ErrorMessage message={errors.renavam.message} />}
                        </div>

                        <div className="flex gap-3 pt-2">
                            <button
                                type="button"
                                onClick={goToPreviousSection}
                                className="flex-1 py-3 border-2 border-gray-200 text-gray-600 font-semibold rounded-xl hover:bg-gray-50 transition-all"
                            >
                                Voltar
                            </button>
                            <button
                                type="button"
                                onClick={goToNextSection}
                                className="flex-1 py-3.5 bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white font-semibold rounded-xl transition-all shadow-lg shadow-emerald-200"
                            >
                                Continuar
                            </button>
                        </div>
                    </div>
                </SectionCard>

                {/* SEÇÃO 4: DADOS PROFISSIONAIS E PIX */}
                <SectionCard
                    title="Dados adicionais e conta para recebimento"
                    icon={<Wallet className="w-5 h-5" />}
                    isOpen={activeSection === 'professional'}
                    onToggle={() => handleSectionToggle('professional')}
                    stepNumber={4}
                    totalSteps={5}
                    completed={completedSections.has('professional')}
                >
                    <div className="space-y-5">
                        {/* Tipo de Chave PIX */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                Tipo de Chave PIX <span className="text-red-500">*</span>
                            </label>
                            <select
                                {...register('pix_key_type')}
                                className={selectClassName(errors.pix_key_type)}
                            >
                                <option value="">Selecione o tipo</option>
                                {PIX_KEY_TYPES.map((type) => (
                                    <option key={type.value} value={type.value}>{type.label}</option>
                                ))}
                            </select>
                            {errors.pix_key_type && <ErrorMessage message={errors.pix_key_type.message} />}
                        </div>

                        {/* Chave PIX */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                Chave PIX <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                {...register('pix_key')}
                                placeholder={getPixPlaceholder()}
                                className={inputClassName(errors.pix_key)}
                            />
                            {errors.pix_key && <ErrorMessage message={errors.pix_key.message} />}
                        </div>

                        {/* Aplicativos de Mobilidade */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Aplicativos de mobilidade <span className="text-red-500">*</span>
                            </label>
                            <p className="text-xs text-gray-500 mb-3">
                                Selecione os aplicativos onde você atua como motorista
                            </p>
                            <div className="flex flex-wrap gap-2">
                                {MOBILITY_APPS.map((app) => (
                                    <TagButton
                                        key={app}
                                        label={app}
                                        selected={mobilityApps.includes(app)}
                                        onClick={() => toggleMobilityApp(app)}
                                    />
                                ))}
                            </div>
                            {errors.mobility_apps && <ErrorMessage message={errors.mobility_apps.message} />}
                        </div>

                        {/* Cidades de Atuação */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Cidades onde costuma dirigir <span className="text-red-500">*</span>
                            </label>
                            <p className="text-xs text-gray-500 mb-3">
                                Selecione as regiões onde você atua
                            </p>
                            <div className="flex flex-wrap gap-2">
                                {AVAILABLE_CITIES.map((city) => (
                                    <TagButton
                                        key={city.value}
                                        label={city.label}
                                        selected={operatingCities.includes(city.value)}
                                        onClick={() => toggleOperatingCity(city.value)}
                                    />
                                ))}
                            </div>
                            {errors.operating_cities && <ErrorMessage message={errors.operating_cities.message} />}
                        </div>

                        {/* Declaração de Motorista Ativo */}
                        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                            <Controller
                                name="confirm_driver_status"
                                control={control}
                                render={({ field }) => (
                                    <label className="flex items-start gap-3 cursor-pointer group">
                                        <div className="relative mt-0.5">
                                            <input
                                                type="checkbox"
                                                checked={field.value}
                                                onChange={field.onChange}
                                                className="peer sr-only"
                                            />
                                            <div className={`w-5 h-5 border-2 rounded transition-all flex items-center justify-center ${field.value
                                                ? 'bg-emerald-500 border-emerald-500'
                                                : 'border-gray-300 group-hover:border-emerald-400'
                                                }`}>
                                                {field.value && <Check className="w-3.5 h-3.5 text-white" />}
                                            </div>
                                        </div>
                                        <span className="text-sm text-gray-700 leading-relaxed">
                                            Confirmo que atualmente exerço a função de motorista de aplicativos de mobilidade urbana,
                                            mantendo a média de no mínimo <strong>12 corridas por dia/semanais</strong>, exercendo a mesma função,
                                            há pelo menos <strong>2 meses</strong>. <span className="text-red-500">*</span>
                                        </span>
                                    </label>
                                )}
                            />
                            {errors.confirm_driver_status && <ErrorMessage message={errors.confirm_driver_status.message} />}
                        </div>

                        <div className="flex gap-3 pt-2">
                            <button
                                type="button"
                                onClick={goToPreviousSection}
                                className="flex-1 py-3 border-2 border-gray-200 text-gray-600 font-semibold rounded-xl hover:bg-gray-50 transition-all"
                            >
                                Voltar
                            </button>
                            <button
                                type="button"
                                onClick={goToNextSection}
                                className="flex-1 py-3.5 bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white font-semibold rounded-xl transition-all shadow-lg shadow-emerald-200"
                            >
                                Continuar
                            </button>
                        </div>
                    </div>
                </SectionCard>

                {/* SEÇÃO 5: DECLARAÇÕES E TERMOS */}
                <SectionCard
                    title="Termos e Declarações"
                    icon={<FileCheck className="w-5 h-5" />}
                    isOpen={activeSection === 'declarations'}
                    onToggle={() => handleSectionToggle('declarations')}
                    stepNumber={5}
                    totalSteps={5}
                    completed={completedSections.has('declarations')}
                >
                    <div className="space-y-4">
                        <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg border border-gray-100">
                            Por favor, leia atentamente e confirme as declarações abaixo para concluir seu cadastro.
                        </p>

                        {/* Declaração 1: Investimento no Tablet */}
                        <div className="bg-white border border-gray-200 rounded-xl p-4 hover:border-emerald-200 transition-colors">
                            <Controller
                                name="confirm_tablet_investment"
                                control={control}
                                render={({ field }) => (
                                    <label className="flex items-start gap-3 cursor-pointer group">
                                        <div className="relative mt-0.5">
                                            <input
                                                type="checkbox"
                                                checked={field.value}
                                                onChange={field.onChange}
                                                className="peer sr-only"
                                            />
                                            <div className={`w-5 h-5 border-2 rounded transition-all flex items-center justify-center ${field.value
                                                ? 'bg-emerald-500 border-emerald-500'
                                                : 'border-gray-300 group-hover:border-emerald-400'
                                                }`}>
                                                {field.value && <Check className="w-3.5 h-3.5 text-white" />}
                                            </div>
                                        </div>
                                        <span className="text-sm text-gray-700 leading-relaxed">
                                            Se aprovado na seleção, estou ciente de que terei que investir em um <strong>tablet
                                                com os requisitos mínimos</strong> exigidos pela Watch Company, para instalação do
                                            aplicativo, a fim de tornar-me um parceiro. <span className="text-red-500">*</span>
                                        </span>
                                    </label>
                                )}
                            />
                            {errors.confirm_tablet_investment && <ErrorMessage message={errors.confirm_tablet_investment.message} />}
                        </div>

                        {/* Declaração 2: Contato por E-mail/Telefone */}
                        <div className="bg-white border border-gray-200 rounded-xl p-4 hover:border-emerald-200 transition-colors">
                            <Controller
                                name="confirm_contact_agreement"
                                control={control}
                                render={({ field }) => (
                                    <label className="flex items-start gap-3 cursor-pointer group">
                                        <div className="relative mt-0.5">
                                            <input
                                                type="checkbox"
                                                checked={field.value}
                                                onChange={field.onChange}
                                                className="peer sr-only"
                                            />
                                            <div className={`w-5 h-5 border-2 rounded transition-all flex items-center justify-center ${field.value
                                                ? 'bg-emerald-500 border-emerald-500'
                                                : 'border-gray-300 group-hover:border-emerald-400'
                                                }`}>
                                                {field.value && <Check className="w-3.5 h-3.5 text-white" />}
                                            </div>
                                        </div>
                                        <span className="text-sm text-gray-700 leading-relaxed">
                                            Estou ciente que, se aprovado, serei informado pelo <strong>e-mail ou número de
                                                telefone</strong> ao qual informei no cadastro. <span className="text-red-500">*</span>
                                        </span>
                                    </label>
                                )}
                            />
                            {errors.confirm_contact_agreement && <ErrorMessage message={errors.confirm_contact_agreement.message} />}
                        </div>

                        {/* Declaração 3: Veracidade das Informações */}
                        <div className="bg-white border border-gray-200 rounded-xl p-4 hover:border-emerald-200 transition-colors">
                            <Controller
                                name="confirm_info_accuracy"
                                control={control}
                                render={({ field }) => (
                                    <label className="flex items-start gap-3 cursor-pointer group">
                                        <div className="relative mt-0.5">
                                            <input
                                                type="checkbox"
                                                checked={field.value}
                                                onChange={field.onChange}
                                                className="peer sr-only"
                                            />
                                            <div className={`w-5 h-5 border-2 rounded transition-all flex items-center justify-center ${field.value
                                                ? 'bg-emerald-500 border-emerald-500'
                                                : 'border-gray-300 group-hover:border-emerald-400'
                                                }`}>
                                                {field.value && <Check className="w-3.5 h-3.5 text-white" />}
                                            </div>
                                        </div>
                                        <span className="text-sm text-gray-700 leading-relaxed">
                                            Autorizo a verificação e declaro que <strong>todas as informações</strong> por mim
                                            descritas são verídicas e se, porventura forem constatadas informações falsas,
                                            estarei desclassificado de acordo com as normas e políticas internas da Watch.
                                            <span className="text-red-500">*</span>
                                        </span>
                                    </label>
                                )}
                            />
                            {errors.confirm_info_accuracy && <ErrorMessage message={errors.confirm_info_accuracy.message} />}
                        </div>

                        <div className="flex gap-3 pt-4">
                            <button
                                type="button"
                                onClick={goToPreviousSection}
                                className="flex-1 py-3 border-2 border-gray-200 text-gray-600 font-semibold rounded-xl hover:bg-gray-50 transition-all"
                            >
                                Voltar
                            </button>
                            <button
                                type="submit"
                                disabled={isLoading}
                                className={`flex-1 py-3.5 font-semibold rounded-xl transition-all flex items-center justify-center gap-2 ${isLoading
                                    ? 'bg-gray-300 cursor-not-allowed text-gray-500'
                                    : 'bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white shadow-lg shadow-emerald-200 hover:shadow-emerald-300'
                                    }`}
                            >
                                {isLoading ? (
                                    <>
                                        <Loader2 className="w-5 h-5 animate-spin" />
                                        Salvando...
                                    </>
                                ) : (
                                    <>
                                        <CheckCircle2 className="w-5 h-5" />
                                        Finalizar
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </SectionCard>
            </form>
        </div>
    );
}

// Componente TagButton para seleção de apps e cidades
function TagButton({ label, selected, onClick }: { label: string; selected: boolean; onClick: () => void }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`px-3 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5 ${selected
                ? 'bg-emerald-500 text-white shadow-md shadow-emerald-200'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200'
                }`}
        >
            {selected ? (
                <Check className="w-3.5 h-3.5" />
            ) : (
                <Plus className="w-3.5 h-3.5" />
            )}
            {label}
        </button>
    );
}

// Componente SectionCard com design verde claro
function SectionCard({
    title,
    icon,
    isOpen,
    onToggle,
    stepNumber,
    totalSteps,
    completed,
    children
}: {
    title: string;
    icon: React.ReactNode;
    isOpen: boolean;
    onToggle: () => void;
    stepNumber: number;
    totalSteps: number;
    completed?: boolean;
    children: React.ReactNode;
}) {
    return (
        <div className={`mb-4 rounded-2xl border-2 transition-all ${isOpen
            ? 'bg-white border-emerald-200 shadow-xl shadow-emerald-100/50'
            : completed
                ? 'bg-emerald-50/50 border-emerald-100 shadow-sm'
                : 'bg-white border-gray-100 shadow-sm'
            }`}>
            <button
                type="button"
                onClick={onToggle}
                className="w-full p-4 flex items-center justify-between text-left"
            >
                <div className="flex items-center gap-3">
                    <div className={`w-11 h-11 rounded-xl flex items-center justify-center transition-all ${isOpen
                        ? 'bg-gradient-to-br from-emerald-500 to-green-600 text-white shadow-lg shadow-emerald-200'
                        : completed
                            ? 'bg-emerald-100 text-emerald-600'
                            : 'bg-gray-100 text-gray-400'
                        }`}>
                        {completed && !isOpen ? <CheckCircle2 className="w-5 h-5" /> : icon}
                    </div>
                    <div>
                        <span className={`font-semibold ${isOpen ? 'text-gray-800' : completed ? 'text-emerald-700' : 'text-gray-500'}`}>
                            {title}
                        </span>
                        <p className="text-xs text-gray-400">Etapa {stepNumber} de {totalSteps}</p>
                    </div>
                </div>
                {isOpen ? (
                    <ChevronUp className="w-5 h-5 text-emerald-500" />
                ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                )}
            </button>

            {isOpen && (
                <div className="px-4 pb-5">
                    {children}
                </div>
            )}
        </div>
    );
}

// Step Indicator Responsivo
function StepIndicator({
    step,
    label,
    active,
    completed
}: {
    step: number;
    label: string;
    active: boolean;
    completed: boolean;
}) {
    return (
        <div className="flex flex-col items-center">
            <div className={`w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center text-xs sm:text-sm font-bold transition-all ${completed
                ? 'bg-emerald-500 text-white'
                : active
                    ? 'bg-emerald-500 text-white ring-4 ring-emerald-100'
                    : 'bg-gray-200 text-gray-400'
                }`}>
                {completed ? <Check className="w-4 h-4" /> : step}
            </div>
            <span className={`text-[10px] sm:text-xs mt-1 font-medium whitespace-nowrap ${active || completed ? 'text-emerald-600' : 'text-gray-400'
                }`}>
                {label}
            </span>
        </div>
    );
}

function ErrorMessage({ message }: { message?: string }) {
    if (!message) return null;
    return (
        <p className="flex items-center gap-1 text-red-500 text-sm mt-1.5">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {message}
        </p>
    );
}

function inputClassName(hasError: any): string {
    return `w-full px-4 py-3 bg-white border-2 rounded-xl text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 transition-all ${hasError
        ? 'border-red-300 focus:ring-red-100 focus:border-red-400'
        : 'border-gray-200 focus:ring-emerald-100 focus:border-emerald-400'
        }`;
}

function selectClassName(hasError: any): string {
    return `w-full px-4 py-3 bg-white border-2 rounded-xl text-gray-800 focus:outline-none focus:ring-2 transition-all appearance-none cursor-pointer ${hasError
        ? 'border-red-300 focus:ring-red-100 focus:border-red-400'
        : 'border-gray-200 focus:ring-emerald-100 focus:border-emerald-400'
        }`;
}

export default DriverOnboarding;
